//윈터스쿨 금액 계산 — 서버 peetsunbaeserver/src/control/winterPrice.ts + data/winterRates.ts(타입)를 그대로 복사 (고객 /winterpay 와 같은 결과)
//계산식을 고칠 때는 서버·suneungclient3 pages/winterpay/price.ts 와 같이 고칠 것. 금액표 값은 서버 GET /dashboard/payments/winterpay/staffconfig 에서 받는다.

export type WinterStudentType = "general" | "student";
export type WinterPeriodTarget = "all" | "student" | "general";
export type WinterBenefitType = "until" | "on" | "always";

export interface WinterDateRange { start: string; end: string; } // "YYYY-MM-DD"

export interface WinterPeriod {
    id: string;
    name: string;
    target: WinterPeriodTarget;
    ranges: WinterDateRange[];
}

export interface WinterBenefit {
    id: string;
    name: string;
    type: WinterBenefitType;
    dates: string[];      // until: [마감일], on: [해당일...], always: []
    pct: number;          // 할인율 %
    periodIds: string[];  // 비어 있으면 등록 기간 전체
    excl?: string[];      // 혜택 제외 지점(english). 없으면 제외 없음
    auto?: boolean;       // false 면 직원 발송 팝업에서 직접 켜야 적용(고객 셀프 결제엔 적용 안 됨). 없으면 자동 적용
}

export interface WinterBranchPrice {
    base: number;                                  // 기본 월 금액
    periods: { [periodId: string]: number | null }; // 기간별 월 금액(없으면 기본)
}

export interface WinterRates {
    prices: { [english: string]: WinterBranchPrice };
    periods: WinterPeriod[];   // 순서 = 우선순위(겹치면 위에 있는 기간)
    benefits: WinterBenefit[];
}

export interface WinterProgram {
    pid: string;           // 기간 id
    name: string;
    startFrom: string;     // 시작일 선택 가능 범위
    startTo: string;
    ends: string[];        // 종료일 선택지
    fixedStart?: { branches: string[]; date: string }; // 특정 지점은 시작일 고정
}

export interface WinterBranch { english: string; korean: string; }


//윈터스쿨 셀프 결제 금액 계산 — 디자인(결제링크 copy.dc.html) 스크립트의 순수 함수를 그대로 포팅
//규칙: 날짜마다 (기간, 월 금액, 할인율)을 정하고 같은 값끼리 묶어 월별 일할(10원 절사)로 계산 후 합산
//프론트(suneungclient3 src/pages/winterpay/price.ts)와 같은 코드. 여기를 고치면 프론트도 같이 고칠 것.

export const isValidPrice = (v: any) => Number.isInteger(v) && v > 0;
export const daysInMonth = (y: number, m: number) => new Date(y, m + 1, 0).getDate(); // m: 0~11
export const floor10 = (v: number) => 10 * Math.floor(v / 10);
export const dayNum = (d: Date) => Math.floor(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 864e5);
export const toDate = (s: string) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
export const fromDayNum = (n: number) => { const d = new Date(n * 864e5); return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()); };
const pad2 = (n: number) => String(n).padStart(2, "0");
export const ymd = (d: Date) => d.getFullYear() + "-" + pad2(d.getMonth() + 1) + "-" + pad2(d.getDate());
export const isYmd = (v: any) => typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v) && ymd(toDate(v)) === v;
const semOk = (p: any) => !!p && isYmd(p.start) && isYmd(p.end) && dayNum(toDate(p.start)) <= dayNum(toDate(p.end));
const toRanges = (list: any[]) => (list || []).filter(semOk).map(p => [dayNum(toDate(p.start)), dayNum(toDate(p.end))] as [number, number]);
const inRanges = (r: [number, number][], n: number) => r.some(([x, y]) => n >= x && n <= y);

//월 금액 일할: 한 달 통째면 월 금액, 아니면 (할인 적용한 월 금액 ÷ 그 달 일수 × 등록일수) 10원 절사
export const calcProrated = (monthlyPrice: number | null, start: Date | null, end: Date | null, discount: number = 0): number | null => {
    if (!isValidPrice(monthlyPrice) || !start || !end) return null;
    if (dayNum(start) > dayNum(end)) return null;
    const base = floor10((monthlyPrice as number) * (100 - discount) / 100); // 할인 먼저 적용 후 10원 절사
    let total = 0;
    let y = start.getFullYear(), m = start.getMonth();
    while (dayNum(new Date(y, m, 1)) <= dayNum(end)) {
        const mStart = new Date(y, m, 1);
        const mEnd = new Date(y, m, daysInMonth(y, m));
        const s = dayNum(start) > dayNum(mStart) ? start : mStart;
        const e = dayNum(end) < dayNum(mEnd) ? end : mEnd;
        if (dayNum(s) <= dayNum(e)) {
            const F = dayNum(e) - dayNum(s) + 1;
            const w = daysInMonth(y, m);
            total += F === w ? base : floor10(base / w * F);
        }
        m++; if (m > 11) { m = 0; y++; }
    }
    return total;
};

//혜택 조건 충족 여부 (payDate = 결제일 "YYYY-MM-DD")
export const benefitMet = (c: WinterBenefit, payDate: string) => {
    if (c.type === "always") return true;
    if (!isYmd(payDate)) return false;
    const pay = dayNum(toDate(payDate));
    return c.type === "until" ? pay <= dayNum(toDate(c.dates[0])) : c.dates.some(d => dayNum(toDate(d)) === pay);
};

export interface RateArgs {
    rates: WinterRates;
    type: WinterStudentType;
    branch: string;
    start: Date | null;
    end: Date | null;
    extra?: number;        // 추가 할인율(관리자용, 고객 화면은 0)
    activeIds?: string[];  // 적용할 혜택 id
    manualPrice?: number;
}

export interface RateSegment {
    from: number; to: number; pid: string | null; name: string; monthly: number; discount: number; bPct: number; benefit: boolean; bIds: string[];
    start: Date; end: Date;
}

export const rateSegments = ({ rates, type, branch, start, end, extra = 0, activeIds = [] }: RateArgs): RateSegment[] => {
    const row = rates && rates.prices[branch];
    if (!row || !start || !end || dayNum(start) > dayNum(end)) return [];
    const ps = rates.periods.map(p => ({ p, r: toRanges(p.ranges) }));
    const act = rates.benefits.filter(c => activeIds.includes(c.id) && !(c.excl || []).includes(branch)); //제외 지점이면 혜택 없음
    const segs: any[] = [];
    for (let n = dayNum(start); n <= dayNum(end); n++) {
        const hit = ps.find(x => (x.p.target === "all" || x.p.target === type) && inRanges(x.r, n));
        const p: WinterPeriod | null = hit ? hit.p : null;
        const monthly = p && isValidPrice(row.periods[p.id]) ? (row.periods[p.id] as number) : row.base;
        const here = act.filter(c => !c.periodIds.length || (p && c.periodIds.includes(p.id)));
        const bPct = here.length ? Math.max(...here.map(c => c.pct)) : 0;
        const discount = Math.max(extra, bPct);
        const pid = p ? p.id : null;
        const last = segs[segs.length - 1];
        if (last && last.pid === pid && last.monthly === monthly && last.discount === discount) last.to = n;
        else segs.push({ from: n, to: n, pid, name: p ? p.name : "기본", monthly, discount, bPct, benefit: bPct > 0, bIds: here.map(c => c.id) });
    }
    return segs.map(g => ({ ...g, start: fromDayNum(g.from), end: fromDayNum(g.to) }));
};

export const calcRate = (args: RateArgs): number | null => {
    if (args.manualPrice) return args.manualPrice; // 수동 금액이 최우선
    const segs = rateSegments(args);
    if (!segs.length) return null;
    let total = 0;
    for (const g of segs) {
        const v = calcProrated(g.monthly, g.start, g.end, g.discount);
        if (v == null) return null;
        total += v;
    }
    return total;
};

//명세서용 월별 내역: calcProrated와 같은 루프로 월별 금액만 펼침
export interface ProrateRow { y: number; m: number; s: Date; e: Date; F: number; w: number; amount: number; }
export const prorateBreakdown = (monthlyPrice: number, start: Date, end: Date, discount: number = 0): ProrateRow[] => {
    if (!isValidPrice(monthlyPrice) || !start || !end || dayNum(start) > dayNum(end)) return [];
    const base = floor10(monthlyPrice * (100 - discount) / 100);
    const rows: ProrateRow[] = [];
    let y = start.getFullYear(), m = start.getMonth();
    while (dayNum(new Date(y, m, 1)) <= dayNum(end)) {
        const mStart = new Date(y, m, 1), mEnd = new Date(y, m, daysInMonth(y, m));
        const s = dayNum(start) > dayNum(mStart) ? start : mStart;
        const e = dayNum(end) < dayNum(mEnd) ? end : mEnd;
        if (dayNum(s) <= dayNum(e)) {
            const F = dayNum(e) - dayNum(s) + 1, w = daysInMonth(y, m);
            rows.push({ y, m, s, e, F, w, amount: F === w ? base : floor10(base / w * F) });
        }
        m++; if (m > 11) { m = 0; y++; }
    }
    return rows;
};

//고객 화면용 한 번에 계산: 오늘 날짜로 혜택 자동 판단, 추가 할인·수동 금액 없음
export interface CustomerPriceResult {
    price: number | null;
    original: number | null;          // 혜택 없는 정가
    activeIds: string[];              // 오늘 조건이 맞는 혜택
    usedBenefits: { id: string; name: string; pct: number }[]; // 실제 구간에 적용된 혜택
    segments: { name: string; pid: string | null; monthly: number; discount: number; start: string; end: string }[];
    rows: { y: number; m: number; name: string; discount: number; start: string; end: string; days: number; monthDays: number; amount: number }[];
}
export const calcCustomerPrice = (rates: WinterRates, type: WinterStudentType, branch: string, start: Date | null, end: Date | null, payDate: string): CustomerPriceResult => {
    //고객 셀프 결제는 '자동 적용' 혜택만 (auto 없으면 자동)
    const activeIds = rates.benefits.filter(c => c.auto !== false && benefitMet(c, payDate)).map(c => c.id);
    const args: RateArgs = { rates, type, branch, start, end, extra: 0, activeIds };
    const price = calcRate(args);
    const original = calcRate({ ...args, activeIds: [] });
    const segs = rateSegments(args);
    const usedIds = new Set<string>();
    segs.forEach(g => g.bIds.forEach(id => usedIds.add(id)));
    const usedBenefits = rates.benefits.filter(c => usedIds.has(c.id)).map(c => ({ id: c.id, name: c.name, pct: c.pct }));
    const rows: CustomerPriceResult["rows"] = [];
    segs.forEach(g => prorateBreakdown(g.monthly, g.start, g.end, g.discount).forEach(r => rows.push({ y: r.y, m: r.m, name: g.name, discount: g.discount, start: ymd(r.s), end: ymd(r.e), days: r.F, monthDays: r.w, amount: r.amount })));
    return {
        price, original, activeIds, usedBenefits,
        segments: segs.map(g => ({ name: g.name, pid: g.pid, monthly: g.monthly, discount: g.discount, start: ymd(g.start), end: ymd(g.end) })),
        rows,
    };
};

//셀프테스트 — 디자인의 runPriceTests/runRateTests 기대값 그대로. node -e "require('./dist/control/winterPrice').runWinterPriceSelfTest()"
